import * as I from 'immutable';
import React from 'react';
import {type Column, useTable} from 'react-table';

import {LoadingSpinner} from 'app/components/AnalyzePolygonChart/AnalyzePolygonChart';
import cs from 'app/components/Dashboard/MultiFeatureAnalysisWidget/MultiFeatureAnalysisTable.module.scss';
import {ApiOrganization} from 'app/modules/Remote/Organization';
import {makeNoteUrlFragment, makeProjectDashboardUrl} from 'app/utils/routeUtils';
import {TAG_KIND, Tag, getTagSettings} from 'app/utils/tagUtils';

export interface DrilldownTableRow {
  organizationId: string;
  featureId: string;
  featureName: string;
  projectId: string;
  projectName: string;
  propertyTags: string[];
  noteId?: number;
  noteTags?: string[];
  shortText?: string;
}

interface LensStatsDrilldownTableProps {
  tableData: DrilldownTableRow[];
  loading?: boolean;
  organization: I.ImmutableOf<ApiOrganization>;
}

export const LensStatsDrilldownTable: React.FC<LensStatsDrilldownTableProps> = ({
  tableData,
  loading = false,
  organization,
}) => {
  const noteTagSettings = getTagSettings(organization, TAG_KIND.NOTE);
  const featureTagSettings = getTagSettings(organization, TAG_KIND.FEATURE);

  const columns = React.useMemo<Column<DrilldownTableRow>[]>(() => {
    const portfolioColumn = {
      Header: 'Portfolio',
      accessor: 'projectName',
      Cell: ({cell}) => {
        const {organizationId, projectId, featureId} = cell.row.original;
        const projectHref = makeProjectDashboardUrl(organizationId, projectId, 'map', [featureId]);
        return (
          <a href={projectHref} target="_blank" rel="noopener noreferrer">
            {cell.value}
          </a>
        );
      },
    };

    const propertyColumn = {
      Header: 'Property',
      accessor: 'featureName',
      Cell: ({cell}) => {
        const {organizationId, featureId, projectId} = cell.row.original;
        const featureHref = makeProjectDashboardUrl(organizationId, projectId, 'map', [featureId]);
        return (
          <a href={featureHref} target="_blank" rel="noopener noreferrer">
            {cell.value}
          </a>
        );
      },
    };

    const propertyTagsColumn = {
      Header: 'Property tags',
      accessor: 'propertyTags',
      Cell: ({cell}) => {
        return (
          <div className={cs.tagItemsContainer}>
            {cell.value?.length > 0 ? (
              cell.value.map((tagText, index) => {
                const tagSetting = featureTagSettings.find((item) => item?.get('text') === tagText);
                return <Tag key={index} setting={tagSetting} />;
              })
            ) : (
              <></>
            )}
          </div>
        );
      },
    };

    const noteTagsColumn = {
      Header: 'Note tags',
      accessor: 'noteTags',
      Cell: ({cell}) => {
        return (
          <div className={cs.tagItemsContainer}>
            {cell.value?.length > 0 ? (
              cell.value.map((tagText, index) => {
                const tagSetting = noteTagSettings.find((item) => item?.get('text') === tagText);
                return <Tag key={index} setting={tagSetting} />;
              })
            ) : (
              <></>
            )}
          </div>
        );
      },
    };

    const shortTextColumn = {
      Header: 'Note',
      accessor: 'shortText',
      Cell: ({cell}) => {
        const {organizationId, featureId, projectId, noteId} = cell.row.original;
        const noteHref = makeProjectDashboardUrl(
          organizationId,
          projectId,
          'map',
          [featureId],
          makeNoteUrlFragment(noteId),
          {includeRoot: true}
        );

        return (
          <div>
            <a href={noteHref} target="_blank" rel="noopener noreferrer">
              {cell.value}
            </a>
          </div>
        );
      },
    };

    if (tableData.some((row) => !!row.noteId)) {
      return [portfolioColumn, propertyColumn, noteTagsColumn, shortTextColumn];
    }

    return [portfolioColumn, propertyColumn, propertyTagsColumn];
  }, [featureTagSettings, noteTagSettings, tableData]);

  const {getTableProps, getTableBodyProps, headerGroups, rows, prepareRow} = useTable({
    columns,
    data: tableData,
  });

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!tableData || tableData.length === 0) {
    return null;
  }

  return (
    <div className={cs.tableContainer} style={{marginTop: '1rem'}}>
      <table {...getTableProps()} className={cs.lensStatsDrilldownTable}>
        <thead style={{backgroundColor: '#F7F8F9', position: 'sticky', top: 0, zIndex: 1}}>
          {headerGroups.map((headerGroup) => {
            const {key, ...headerGroupProps} = headerGroup.getHeaderGroupProps();
            return (
              <tr key={key} {...headerGroupProps}>
                {headerGroup.headers.map((column) => {
                  const {key: columnKey, ...headerProps} = column.getHeaderProps();
                  return (
                    <th key={columnKey} {...headerProps}>
                      {column.render('Header')}
                    </th>
                  );
                })}
              </tr>
            );
          })}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);
            const {key, ...rowProps} = row.getRowProps();
            return (
              <tr key={key} {...rowProps} style={{borderBottom: '1px solid #E0E0E0'}}>
                {row.cells.map((cell) => {
                  const {key: cellKey, ...cellProps} = cell.getCellProps();
                  return (
                    <td key={cellKey} {...cellProps}>
                      {cell.render('Cell')}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
